import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Bot, Search, Megaphone, FileText, Brain, Zap, TrendingUp, MessageSquare, LucideIcon } from "lucide-react";

interface AnimatedIconProps {
  icon: "bot" | "search" | "megaphone" | "file" | "brain" | "zap" | "trending" | "message";
  className?: string;
  size?: "sm" | "md" | "lg";
}

const iconMap: Record<string, LucideIcon> = {
  bot: Bot,
  search: Search,
  megaphone: Megaphone,
  file: FileText,
  brain: Brain,
  zap: Zap,
  trending: TrendingUp,
  message: MessageSquare,
};

const sizeMap = {
  sm: { container: "w-16 h-16", icon: "w-8 h-8", ring: 80 },
  md: { container: "w-24 h-24", icon: "w-12 h-12", ring: 120 },
  lg: { container: "w-32 h-32", icon: "w-16 h-16", ring: 160 },
};

export function AnimatedIcon({ icon, className, size = "md" }: AnimatedIconProps) {
  const Icon = iconMap[icon];
  const sizes = sizeMap[size];
  
  return (
    <motion.div 
      className={cn("flex items-center justify-center", className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="relative">
        {/* Animated rings - CSS-based for performance */}
        <div
          className="absolute rounded-full border-2 border-primary/30 animate-ping"
          style={{ 
            width: sizes.ring, 
            height: sizes.ring, 
            left: `calc(50% - ${sizes.ring/2}px)`, 
            top: `calc(50% - ${sizes.ring/2}px)`,
            animationDuration: '3s'
          }}
        />
        
        {/* Main icon container */}
        <motion.div
          className={cn(
            "relative rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center backdrop-blur-sm border border-primary/20 will-change-transform",
            sizes.container
          )}
          animate={{ 
            y: [0, -6, 0],
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        >
          <Icon className={cn("text-primary", sizes.icon)} />
        </motion.div>
        
        {/* Static decorative dots */}
        <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full bg-primary/40" />
        <div className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-primary/30" />
      </div>
    </motion.div>
  );
}
